import { arrayItemUpdater, fieldUpdater, partialUpdater } from '@shared/Frontend/utils/updaters'
import { Viewport } from '@shared/Frontend/utils/viewport'
import { Template } from 'ballerina-core'
import React, { ReactNode } from 'react'
import {
  Block,
  BlocksState,
  BmiCalculatorBlock,
  ContactBlock,
  ContactGridBlock,
  CTABlock,
  DownloadBlock,
  ExpandableBlock,
  FactsAndFiguresBlock,
  FeaturedContentBlock,
  GridBlock,
  Iframe,
  LinkListBlock,
  LogoWallBlock,
  MediaBlock,
  QuoteBlock,
  RecentContentBlock,
  RichDownloadsBlock,
  SidebarNavigationBlock,
  TableBlock,
  TextBlock,
  TextReadMoreBlock,
  TextWithImage,
} from './blocks.state'
import { MediaGalleryBlockState } from './mediaGalleryBlock/mediaGalleryBlock.state'
import { MediaGalleryBlockTemplate, MediaGalleryBlockView } from './mediaGalleryBlock/mediaGalleryBlock.template'
import { MappedRecentContentBlockTemplate } from './recentContent/recentContent.template'
import { CMSPageView } from '../cmsPage.template'

export type BlocksContext = { videoCookieAccepted: boolean; trackingCookieAccepted: boolean; viewport: Viewport; printMode: boolean }
export type BlocksForeignMutationsExpected = { acceptVideoCookie: () => void; acceptTrackingCookie: () => void }
export type BlocksView = {
  BmiCalculatorBlock: (_: BmiCalculatorBlock & { id: string } ) => ReactNode
  ContactBlock: (_: ContactBlock & { id: string }) => ReactNode
  ContactGridBlock: (_: ContactGridBlock & { id: string }) => ReactNode
  CTABlock: (_: CTABlock & { id: string }) => ReactNode
  DownloadBlock: (_: DownloadBlock & { id: string }) => ReactNode
  ExpandableBlock: (
    _: ExpandableBlock & {
      printMode: boolean
      videoCookieAccepted: boolean
      acceptVideoCookie: () => void
      id: string
    }
  ) => ReactNode
  FactsAndFiguresBlock: (_: FactsAndFiguresBlock & { id: string }) => ReactNode
  FeaturedContentBlock: (_: FeaturedContentBlock & { id: string }) => ReactNode
  GridBlock: (_: GridBlock & { id: string }) => ReactNode
  SimpleGridBlock: (_: GridBlock & { id: string }) => ReactNode
  MediaBlock: (
    _: MediaBlock & {
      videoCookieAccepted: boolean
      acceptVideoCookie: () => void
      id: string
    }
  ) => ReactNode
  TextWithImageBlock: (_: TextWithImage & { id: string }) => ReactNode
  LinkListBlock: (_: LinkListBlock & { id: string }) => ReactNode
  LogoWallBlock: (_: LogoWallBlock & { id: string }) => ReactNode
  QuoteBlock: (_: QuoteBlock & { id: string }) => ReactNode
  RecentContentBlock: (_: RecentContentBlock) => ReactNode
  RichDownloadsBlock: (_: RichDownloadsBlock & { id: string }) => ReactNode
  SidebarNavigationBlock: (_: SidebarNavigationBlock & { id: string }) => ReactNode
  TableBlock: (_: TableBlock & { id: string }) => ReactNode
  TextBlock: (_: TextBlock & { id: string }) => ReactNode
  TextMiniBlock: (_: TextBlock & { id: string }) => ReactNode
  TextReadMoreBlock: (
    _: TextReadMoreBlock & {
      printMode: boolean
      videoCookieAccepted: boolean
      acceptVideoCookie: () => void
      id: string
    }
  ) => ReactNode
  IframeBlock: (_: Iframe & {
    id: string
    videoCookieAccepted: boolean
    acceptVideoCookie: () => void
    trackingCookieAccepted: boolean
    acceptTrackingCookie: () => void
 }) => ReactNode
} & MediaGalleryBlockView
export type BlockProps<k extends keyof BlocksView> = Parameters<BlocksView[k]>[0]

export const BlocksTemplate = Template.Default<BlocksContext, BlocksState, BlocksForeignMutationsExpected, BlocksView>(
  ({ context, setState, foreignMutations, view }) => (
    <>
      {context.blocks.map((block, index) => {
        const id = `${block.kind}-${index}`
        switch (block.kind) {
          case 'bmiCalculator':
            return <view.BmiCalculatorBlock {...block} id={id} key={index} />
          case 'contactBlock':
            return <view.ContactBlock {...block} id={id} key={index} />
          case 'contactGridBlock':
            return <view.ContactGridBlock {...block} id={id} key={index} />
          case 'ctaBlock':
            return <view.CTABlock {...block} id={id} key={index} />
          case 'downloadBlock':
            return <view.DownloadBlock {...block} id={id} key={index} />
          case 'expandableBlock':
            return (
              <view.ExpandableBlock
                {...block}
                printMode={context.printMode}
                videoCookieAccepted={context.videoCookieAccepted}
                acceptVideoCookie={foreignMutations.acceptVideoCookie}
                id={id}
                key={index}
              />
            )
          case 'factsAndFiguresBlock':
            return <view.FactsAndFiguresBlock {...block} id={id} key={index} />
          case 'featuredContentBlock':
            return <view.FeaturedContentBlock {...block} id={id} key={index} />
          case 'gridBlock':
            return <view.GridBlock {...block} id={id} key={index} />
          case 'gridSimpleBlock':
            return <view.SimpleGridBlock {...block} id={id} key={index} />
          case 'iframe':
            return (
              <view.IframeBlock
                {...block}
                id={id}
                key={index}
                videoCookieAccepted={context.videoCookieAccepted}
                acceptVideoCookie={foreignMutations.acceptVideoCookie}
                trackingCookieAccepted={context.trackingCookieAccepted}
                acceptTrackingCookie={foreignMutations.acceptTrackingCookie}
              />
            )
          case 'linkListBlock':
            return <view.LinkListBlock {...block} id={id} key={index} />
          case 'logoWallBlock':
            return <view.LogoWallBlock {...block} id={id} key={index} />
          case 'mediaBlock':
            return (
              <view.MediaBlock
                {...block}
                videoCookieAccepted={context.videoCookieAccepted}
                acceptVideoCookie={foreignMutations.acceptVideoCookie}
                id={id}
                key={index}
              />
            )
          case 'mediaGalleryBlock':
            return (
              <MappedMediaGalleryBlockTemplate
                context={{ ...context, ...block, id }}
                setState={(updater) =>
                  setState(fieldUpdater<BlocksState>()('blocks')(arrayItemUpdater(index, updater)))
                }
                foreignMutations={foreignMutations}
                view={view}
                key={index}
              />
            )
          case 'quoteBlock':
            return <view.QuoteBlock {...block} id={id} key={index} />
          case 'recentContentBlock':
            return <MappedRecentContentBlockTemplate
              context={{...context, ...block, id}}
              setState={(updater) =>
                setState(fieldUpdater<BlocksState>()('blocks')(arrayItemUpdater(index, updater)))
              }
              foreignMutations={foreignMutations}
              view={view as CMSPageView}
              key={index}
            />
          case 'richDownloadsBlock':
            return <view.RichDownloadsBlock {...block} id={id} key={index} />
          case 'sidebarNavigation':
            return <view.SidebarNavigationBlock {...block} id={id} key={index} />
          case 'tableBlock':
            return <view.TableBlock {...block} id={id} key={index} />
          case 'textBlock':
            return <view.TextBlock {...block} id={id} key={index} />
          case 'textMiniBlock':
            return <view.TextMiniBlock {...block} id={id} key={index} />
          case 'textReadMoreBlock':
            return <view.TextReadMoreBlock {...context} {...foreignMutations} {...block} id={id} key={index} />
          case 'textWithImageBlock':
            return <view.TextWithImageBlock {...block} id={id} key={index} />
        }
      })}
    </>
  )
)

const MappedMediaGalleryBlockTemplate = MediaGalleryBlockTemplate.mapState<Block>(
  (updater) =>
    (state): Block =>
      state.kind == 'mediaGalleryBlock' ? partialUpdater<MediaGalleryBlockState>(updater)(state) : state
)
